import { cond, get, isEmpty, stubTrue } from 'lodash/fp';
import type {
  HandleGetNewCode,
  HandleResendEmailConfirmation,
  HandleRestorePassword,
  LoginConfig,
  SetFormType,
  SetSharedState,
} from 'src/components/AuthNew/types';
import { AuthFormType } from 'src/components/AuthNew/types';
import { api } from 'src/api';
import { flowAsync } from 'src/utils/fp';
import type {
  getClientExists,
  getClientExistsResponse,
  GetClientResendEmailConfirmationResponse,
  PostClientRestorePasswordResponse,
  PostClientSendOtpSmsResponse,
} from '@lt/api/dist/methods/client/types';
import type { CondPairUnary } from 'lodash';
import {
  isPhoneConfirm,
  isPhoneNotConfirm,
  isPasswordSet,
  isPasswordNotSet,
  mergeFullClientDataWithSharedState,
  openConfirmAndLoginPhoneWithSMSCode,
  openLoginWithPassword,
  openLoginWithSMSCode,
  isLoginWithUnconfirmedEmail,
  isLoginWithConfirmedEmail,
  isPasswordLeaked,
} from 'src/components/AuthNew/utils';
import { useRecaptchaController } from 'src/components/ReCaptchaModal';

type Props = {
  setFormType: SetFormType;
  handleGetNewCode: HandleGetNewCode;
  handleRestorePassword: HandleRestorePassword;
  handleResendEmailConfirmation: HandleResendEmailConfirmation;
  setSharedState: SetSharedState;
};

const isClientExist = (response: getClientExistsResponse) =>
  response.success && !isEmpty(response?.client);

const ifClientExist = (
  phone: string | undefined,
  email: string | undefined,
  setFormType: SetFormType,
  handleGetNewCode: Props['handleGetNewCode'],
  handleRestorePassword: Props['handleRestorePassword'],
  handleResendEmailConfirmation: Props['handleResendEmailConfirmation'],
  setSharedState: SetSharedState,
): CondPairUnary<getClientExistsResponse, Promise<boolean> | undefined> => [
  isClientExist,
  getPhoneLoginCodeCond(
    phone,
    email,
    setFormType,
    handleGetNewCode,
    handleRestorePassword,
    handleResendEmailConfirmation,
    setSharedState,
  ),
];

const isClientNotExist = (response: getClientExistsResponse): boolean =>
  get('success')(response) === false;

const openRegistrationForm = (setFormType: SetFormType) => (): undefined => {
  setFormType(AuthFormType.Registration);
  return undefined;
};

const ifClientNotExist =
  (setSharedState: SetSharedState) =>
  (setFormType: SetFormType) =>
  ({
    phone,
    type,
    email,
  }: LoginConfig): CondPairUnary<getClientExistsResponse, undefined> =>
    [
      isClientNotExist,
      () => {
        setSharedState((state) => ({
          ...state,
          email: email ?? state.email,
          phone: phone ?? state.phone,
          signUpType: type,
        }));
        openRegistrationForm(setFormType)();
        return undefined;
      },
    ];

const ifAuthByPhone = (phone: string | undefined) => () => Boolean(phone);
const ifAuthByEmail = stubTrue;

const ifPhoneConfirm =
  (setSharedState: SetSharedState) =>
  (setFormType: SetFormType) =>
  (handleGetNewCode: Props['handleGetNewCode']) =>
  (
    phone: string | undefined,
  ): CondPairUnary<
    getClientExistsResponse,
    Promise<PostClientSendOtpSmsResponse>
  > =>
    [
      isPhoneConfirm,
      (response) => {
        setSharedState(mergeFullClientDataWithSharedState(response.client));
        return flowAsync(
          handleGetNewCode,
          openLoginWithSMSCode(setFormType),
        )({
          phone,
          event: 'sign_in',
        });
      },
    ];

const ifPhoneNotConfirm =
  (setFormType: SetFormType) =>
  (handleGetNewCode: Props['handleGetNewCode']) =>
  (setSharedState: SetSharedState) =>
  (
    phone: string | undefined,
  ): CondPairUnary<
    getClientExistsResponse,
    Promise<PostClientSendOtpSmsResponse>
  > =>
    [
      isPhoneNotConfirm,
      (result: getClientExistsResponse) =>
        flowAsync(
          setSharedState,
          () =>
            handleGetNewCode({
              client_id: result.client_id || undefined,
              phone: phone || '',
              event: 'sign_in',
            }),
          openConfirmAndLoginPhoneWithSMSCode(setFormType),
        )(mergeFullClientDataWithSharedState(result.client || undefined)),
    ];

const getNewPhoneCodeCond = (
  phone: string | undefined,
  setFormType: SetFormType,
  handleGetNewCode: Props['handleGetNewCode'],
  setSharedState: SetSharedState,
) =>
  flowAsync(
    cond<getClientExistsResponse, Promise<PostClientSendOtpSmsResponse>>([
      ifPhoneConfirm(setSharedState)(setFormType)(handleGetNewCode)(phone),
      ifPhoneNotConfirm(setFormType)(handleGetNewCode)(setSharedState)(phone),
    ]),
  );

const ifPasswordSet =
  (handleResendEmailConfirmation: Props['handleResendEmailConfirmation']) =>
  (setSharedState: SetSharedState) =>
  (setFormType: SetFormType) =>
  (
    email: string | undefined,
  ): CondPairUnary<
    getClientExistsResponse,
    | Promise<
        CondPairUnary<
          getClientExistsResponse,
          Promise<GetClientResendEmailConfirmationResponse>
        >
      >
    | undefined
  > =>
    [
      isPasswordSet,
      getPasswordSetCond(
        email,
        setFormType,
        handleResendEmailConfirmation,
        setSharedState,
      ),
    ];

const getPasswordSetCond = (
  email: string | undefined,
  setFormType: SetFormType,
  handleResendEmailConfirmation: Props['handleResendEmailConfirmation'],
  setSharedState: SetSharedState,
) =>
  flowAsync(
    cond<
      getClientExistsResponse,
      Promise<PostClientRestorePasswordResponse | boolean> | undefined
    >([
      ifLoginWithUnconfirmedEmail(handleResendEmailConfirmation)(email),
      ifLoginWithConfirmedEmail(setFormType)(setSharedState)(email),
    ]),
  );

const ifPasswordNotSet =
  (handleRestorePassword: Props['handleRestorePassword']) =>
  (
    email: string | undefined,
  ): CondPairUnary<
    getClientExistsResponse,
    Promise<PostClientRestorePasswordResponse>
  > =>
    [isPasswordNotSet, () => handleRestorePassword(email, false)];

const ifPasswordLeaked =
  (handleRestorePassword: Props['handleRestorePassword']) =>
  (
    email: string | undefined,
  ): CondPairUnary<
    getClientExistsResponse,
    Promise<PostClientRestorePasswordResponse>
  > =>
    [isPasswordLeaked, () => handleRestorePassword(email, true)];

const ifLoginWithConfirmedEmail =
  (setFormType: SetFormType) =>
  (setSharedState: SetSharedState) =>
  (
    email: string | undefined,
  ): CondPairUnary<getClientExistsResponse, Promise<boolean> | undefined> =>
    [
      (response) => isLoginWithConfirmedEmail(response, email),
      (response) => {
        setSharedState(mergeFullClientDataWithSharedState(response.client));
        openLoginWithPassword(setFormType)();
        return undefined;
      },
    ];

const ifLoginWithUnconfirmedEmail =
  (handleResendEmailConfirmation: Props['handleResendEmailConfirmation']) =>
  (
    email: string | undefined,
  ): CondPairUnary<
    getClientExistsResponse,
    Promise<GetClientResendEmailConfirmationResponse>
  > =>
    [
      (response) => isLoginWithUnconfirmedEmail(response, email),
      () => handleResendEmailConfirmation(email),
    ];

const getEmailLoginCond = (
  email: string | undefined,
  setFormType: SetFormType,
  handleRestorePassword: Props['handleRestorePassword'],
  handleResendEmailConfirmation: Props['handleResendEmailConfirmation'],
  setSharedState: SetSharedState,
) =>
  flowAsync(
    cond<
      getClientExistsResponse,
      | Promise<
          | PostClientRestorePasswordResponse
          | GetClientResendEmailConfirmationResponse
          | boolean
          | CondPairUnary<
              getClientExistsResponse,
              Promise<GetClientResendEmailConfirmationResponse>
            >
        >
      | undefined
    >([
      ifPasswordSet(handleResendEmailConfirmation)(setSharedState)(setFormType)(
        email,
      ),
      ifPasswordLeaked(handleRestorePassword)(email),
      ifPasswordNotSet(handleRestorePassword)(email),
    ]),
  );

const getPhoneLoginCodeCond = (
  phone: string | undefined,
  email: string | undefined,
  setFormType: SetFormType,
  handleGetNewCode: Props['handleGetNewCode'],
  handleRestorePassword: Props['handleRestorePassword'],
  handleResendEmailConfirmation: Props['handleResendEmailConfirmation'],
  setSharedState: SetSharedState,
) =>
  cond<getClientExistsResponse, Promise<boolean> | undefined>([
    [
      ifAuthByPhone(phone),
      getNewPhoneCodeCond(phone, setFormType, handleGetNewCode, setSharedState),
    ],
    [
      ifAuthByEmail,
      getEmailLoginCond(
        email,
        setFormType,
        handleRestorePassword,
        handleResendEmailConfirmation,
        setSharedState,
      ),
    ],
  ]);

const getClientExistsRequest = (
  data: getClientExists,
): Promise<getClientExistsResponse> => api.getClientExists(data);

export const useLogin = ({
  handleGetNewCode,
  handleRestorePassword,
  handleResendEmailConfirmation,
  setFormType,
  setSharedState,
}: Props) => {
  const { isOpenReCaptcha, captchaCallback, handleRecaptchaRequired } =
    useRecaptchaController();
  async function handleLogin({
    email,
    phone,
    type,
  }: LoginConfig): Promise<void> {
    const clientExistRequest = {
      email: type === 'email' ? email : undefined,
      phone: type === 'phone' ? phone : undefined,
    };

    try {
      const response = await getClientExistsRequest(clientExistRequest);

      if (!response.success && response.code === 'captcha_required') {
        handleRecaptchaRequired(() => handleLogin({ email, phone, type }));
        return;
      }

      await cond<getClientExistsResponse, Promise<boolean> | undefined>([
        ifClientExist(
          phone,
          email,
          setFormType,
          handleGetNewCode,
          handleRestorePassword,
          handleResendEmailConfirmation,
          setSharedState,
        ),
        ifClientNotExist(setSharedState)(setFormType)({ email, phone, type }),
      ])(response);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setFormType(AuthFormType.Failure);
    }
  }

  return { handleLogin, isOpenReCaptcha, captchaCallback };
};
